'use client';

/**
 * `useTurnkeySessionSteward` — platform-neutral core of the Turnkey session
 * steward. The web and RN `TurnkeySessionSteward` components bind their
 * vendor kit's `useTurnkey()` surface into `TurnkeyKitBindings` and mount
 * this hook; everything else (guards, decision, throttle stamp, telemetry,
 * logout fan-out) is shared here.
 *
 * See `session-maintenance.ts` for the WHY and the pure decision core. This
 * hook owns the IO around it:
 *   - triggers: kit-ready, app foreground (`visible`/`resume`/
 *     `bfcache-restore`), connectivity restored, and the RN-owned scheduled
 *     refresh threshold
 *   - session reads via the kit's `getSession()` (storage-backed — never the
 *     per-tab `session` React state, which goes stale across tabs)
 *   - the proactive `refreshSession()` call, stamped through
 *     `adapter.storage` so concurrent tabs don't stampede (each refresh
 *     mints a new keypair and clobbers the stored session)
 *   - the reconcile logout: settle delay → re-run every guard against a
 *     fresh `getSession()` → `signalSessionExpired()`
 *
 * Failure posture: on RN, a failed `refreshSession()` schedules another check
 * after one minute while the stored session remains valid. An expired session
 * follows the reconcile path. Telemetry flows through `onAuthEvent` (the host
 * maps names to its typed event system / Better Stack).
 */
import { useCallback, useEffect, useRef } from 'react';
import { useSdkRuntime } from '../../provider/SdkRuntimeContext.js';
import { useAuthSync } from '../oauth/auth-sync.js';
import { SESSION_TTL_MS } from '../session-policy.js';
import { getSdkAuthStore } from '../state/auth-state.js';
import { decideTurnkeySessionAction, isTurnkeySessionValid, RECONCILE_SETTLE_MS } from './session-maintenance.js';
import { useTurnkeyModalActive } from './turnkey-modal-context.js';
/** Throttle-stamp key in `adapter.storage.sync`. */
export const TURNKEY_LAST_REFRESH_STORAGE_KEY = 'morfi:tk:lastRefreshAt';
/** Policy TTL, in the string-seconds form Turnkey expects, when the kit
 *  config carries none. Policy first, not the session's own TTL, so a
 *  session with a shorter TTL returns to policy on its next refresh. */
const FALLBACK_SESSION_EXPIRATION_SECONDS = String(SESSION_TTL_MS / 1000);
function readLastRefreshAtMs(getItem) {
  try {
    const raw = getItem(TURNKEY_LAST_REFRESH_STORAGE_KEY);
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    // SILENT-CATCH: storage may be blocked — treat as never-refreshed.
    return null;
  }
}
function sleep(ms) {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}
/**
 * Best-effort delete of the key `previous` stamped with. Returns 'deleted',
 * 'absent' (already gone or unknown), 'failed' (the delete call threw) or
 * 'skipped' (no client or no key identity on the snapshot). A failure never
 * touches the session that was just minted, so it is reported, not thrown.
 */
async function pruneRetiredKey(kit, previous) {
  if (kit.pruneSessionKey === undefined || previous?.userId === undefined || previous.publicKey === undefined) {
    return 'skipped';
  }
  try {
    const deleted = await kit.pruneSessionKey({
      userId: previous.userId,
      publicKey: previous.publicKey
    });
    return deleted ? 'deleted' : 'absent';
  } catch {
    // SILENT-CATCH: the retired key expires on its own within the session TTL;
    // the outcome rides on the refresh event so a failing prune is visible.
    return 'failed';
  }
}
/**
 * Mounts the steward's trigger listeners and check loop. Returns nothing —
 * the platform component renders `null` around it.
 */
export function useTurnkeySessionSteward(options) {
  const {
    adapter
  } = useSdkRuntime();
  const nextCheckDelayMs = options.nextCheckDelayMs;
  const {
    peekFreshLogin,
    isCompletingOauth,
    signalSessionExpired
  } = useAuthSync();
  const modalActive = useTurnkeyModalActive();
  // Latest-value mirror for the async check path — the kit bindings and the
  // modal flag change across renders, but the trigger listeners are
  // long-lived. Synced in an effect (never written during render).
  const latestRef = useRef({
    kit: options.kit,
    modalActive,
    onAuthEvent: options.onAuthEvent
  });
  useEffect(() => {
    latestRef.current = {
      kit: options.kit,
      modalActive,
      onAuthEvent: options.onAuthEvent
    };
  });
  const inFlightRef = useRef(false);
  // A trigger that lands mid-check re-runs once the check settles: on RN the
  // scheduled timer is the only refresh source while the app stays open, and
  // the in-flight check may have read state from before that trigger.
  const pendingTriggerRef = useRef(null);
  const mountedRef = useRef(false);
  const scheduledCheckRef = useRef(null);
  // One read-failure event per failure streak — the retry runs every minute.
  const readFailedRef = useRef(false);
  // One reconcile logout per detection — reset when a live session is seen
  // again (i.e. the user logged back in).
  const reconcileFiredRef = useRef(false);
  const buildInput = useCallback((session, nowMs) => {
    const latest = latestRef.current;
    const auth = getSdkAuthStore().current();
    return {
      session: session ?? null,
      nowMs,
      online: adapter.netInfo.current().online,
      clientReady: latest.kit.clientReady,
      authedWithSubOrg: auth.status === 'authed' && auth.user?.turnkeySubOrgId != null,
      freshLoginPending: peekFreshLogin() !== null,
      completingOauth: isCompletingOauth(),
      modalActive: latest.modalActive,
      lastRefreshAtMs: readLastRefreshAtMs(key => adapter.storage.sync.getItem(key))
    };
  }, [adapter, peekFreshLogin, isCompletingOauth]);
  const scheduleCheck = useCallback((delayMs, onDue) => {
    // A check that settles after unmount must not arm a timer nobody clears.
    if (!mountedRef.current) return;
    if (scheduledCheckRef.current !== null) clearTimeout(scheduledCheckRef.current);
    scheduledCheckRef.current = setTimeout(() => {
      scheduledCheckRef.current = null;
      onDue();
    }, delayMs);
  }, []);
  const scheduleSessionCheck = useCallback((session_0, nowMs_0, onDue_0) => {
    if (nextCheckDelayMs === undefined) return;
    const delayMs_0 = nextCheckDelayMs(buildInput(session_0, nowMs_0));
    if (delayMs_0 !== null) scheduleCheck(delayMs_0, onDue_0);
  }, [buildInput, nextCheckDelayMs, scheduleCheck]);
  const runCheck = useCallback(async trigger => {
    if (inFlightRef.current) {
      pendingTriggerRef.current = trigger;
      return;
    }
    inFlightRef.current = true;
    try {
      const latest_0 = latestRef.current;
      const session_1 = await latest_0.kit.getSession();
      readFailedRef.current = false;
      const nowMs_1 = Date.now();
      if (isTurnkeySessionValid(session_1 ?? null, nowMs_1)) {
        reconcileFiredRef.current = false;
      }
      const action = decideTurnkeySessionAction(buildInput(session_1, nowMs_1));
      if (action === 'refresh') {
        // Stamp BEFORE the call — a concurrent tab's check sees the stamp
        // and skips, so only one tab mints the replacement session.
        try {
          adapter.storage.sync.setItem(TURNKEY_LAST_REFRESH_STORAGE_KEY, String(nowMs_1));
        } catch {
          // SILENT-CATCH: storage may be blocked — refresh anyway.
        }
        try {
          await latest_0.kit.refreshSession({
            // A blank kit value would reach core as no TTL and revive its
            // 15-minute default, so only a non-empty string counts.
            expirationSeconds: latest_0.kit.sessionExpirationSeconds?.trim() || FALLBACK_SESSION_EXPIRATION_SECONDS
          });
        } catch {
          if (nextCheckDelayMs !== undefined) {
            // A transient failure minted no replacement. Preserve a newer
            // stamp from another check, but clear ours so RN can retry.
            try {
              if (adapter.storage.sync.getItem(TURNKEY_LAST_REFRESH_STORAGE_KEY) === String(nowMs_1)) {
                adapter.storage.sync.removeItem(TURNKEY_LAST_REFRESH_STORAGE_KEY);
              }
            } catch {
              // SILENT-CATCH: blocked storage cannot prevent the retry check.
            }
          }
          latest_0.onAuthEvent?.('turnkey_refresh_failed', {
            reason: 'provider-refresh-failed',
            trigger
          });
          if (nextCheckDelayMs !== undefined) {
            scheduleCheck(60_000, () => void runCheck('scheduled'));
          }
          return;
        }
        // The refresh minted a new key and left the old one on the Turnkey
        // user. Retire it now, stamped with the new session, so the
        // ten-key cap never evicts a key another device still signs with.
        const pruned = await pruneRetiredKey(latest_0.kit, session_1);
        latest_0.onAuthEvent?.('turnkey_session_refreshed', {
          trigger,
          pruned
        });
        // Re-read the kit's stored session after its refresh settles. The
        // next check then uses the actual new expiry, including short TTLs.
        if (nextCheckDelayMs !== undefined) {
          scheduleCheck(60_000, () => void runCheck('scheduled'));
        }
        return;
      }
      if (action === 'none') {
        scheduleSessionCheck(session_1, nowMs_1, () => void runCheck('scheduled'));
      }
      if (action === 'reconcile-logout' && !reconcileFiredRef.current) {
        // Settle, then re-run EVERY guard against a fresh storage read —
        // an in-flight login/refresh (this tab or another) clears here.
        await sleep(RECONCILE_SETTLE_MS);
        const recheckSession = await latestRef.current.kit.getSession();
        const recheck = decideTurnkeySessionAction(buildInput(recheckSession, Date.now()));
        if (recheck !== 'reconcile-logout') {
          scheduleSessionCheck(recheckSession, Date.now(), () => void runCheck('scheduled'));
          return;
        }
        reconcileFiredRef.current = true;
        latestRef.current.onAuthEvent?.('turnkey_session_reconcile_logout', {
          trigger
        });
        signalSessionExpired('turnkey-reconcile');
      }
    } catch {
      // Steward checks are best-effort background work — a failed storage
      // read must never crash the app. Keep a retry armed if a scheduled
      // read consumed the only timer for this session.
      if (!readFailedRef.current) {
        readFailedRef.current = true;
        latestRef.current.onAuthEvent?.('turnkey_refresh_failed', {
          reason: 'session-read-failed',
          trigger
        });
      }
      const retryInput = buildInput(undefined, Date.now());
      if (nextCheckDelayMs !== undefined && retryInput.clientReady && retryInput.authedWithSubOrg) {
        scheduleCheck(60_000, () => void runCheck('scheduled'));
      }
    } finally {
      inFlightRef.current = false;
      const pending = pendingTriggerRef.current;
      pendingTriggerRef.current = null;
      if (pending !== null && mountedRef.current) void runCheck(pending);
    }
  }, [adapter, buildInput, nextCheckDelayMs, scheduleCheck, scheduleSessionCheck, signalSessionExpired]);
  const clientReady = options.kit.clientReady;
  const foregroundDelayMs = options.foregroundDelayMs ?? 0;
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    const authStore = getSdkAuthStore();
    let wasTurnkeyAuthed = authStore.current().status === 'authed' && authStore.current().user?.turnkeySubOrgId != null;
    return authStore.subscribe(() => {
      const auth_0 = authStore.current();
      const isTurnkeyAuthed = auth_0.status === 'authed' && auth_0.user?.turnkeySubOrgId != null;
      if (isTurnkeyAuthed && !wasTurnkeyAuthed) void runCheck('auth-restored');
      wasTurnkeyAuthed = isTurnkeyAuthed;
    });
  }, [runCheck]);
  useEffect(() => {
    if (!clientReady) return;
    void runCheck('ready');
    let foregroundCheck = null;
    const removeAppListener = adapter.appState.addListener(event => {
      if (event.kind !== 'visible' && event.kind !== 'resume' && event.kind !== 'bfcache-restore') {
        return;
      }
      if (foregroundDelayMs > 0) {
        if (foregroundCheck !== null) clearTimeout(foregroundCheck);
        foregroundCheck = setTimeout(() => {
          foregroundCheck = null;
          void runCheck('foreground');
        }, foregroundDelayMs);
      } else {
        void runCheck('foreground');
      }
    });
    const removeNetListener = adapter.netInfo.addListener(online => {
      if (online) void runCheck('online');
    });
    return () => {
      if (scheduledCheckRef.current !== null) clearTimeout(scheduledCheckRef.current);
      if (foregroundCheck !== null) clearTimeout(foregroundCheck);
      removeAppListener();
      removeNetListener();
    };
  }, [clientReady, foregroundDelayMs, adapter, runCheck]);
}